/**
 * AdminConnectionsMonitor
 *
 * Super-admin read-only view of all tenants' connection health.
 * Shows WhatsApp and calendar status per company.
 *
 * Rules:
 *  - Super-admin sees this in Admin /admin/connections panel
 *  - Read-only: NO connect/disconnect buttons shown here
 *  - Calendar section only shows status (connected/not + provider)
 *  - WhatsApp section shows per-connection status breakdown
 *
 * API: GET /api/admin/connections-overview
 *
 * Visual language: CRM cardStackMode v3 — WhatsApp green accent
 * (#128c7e light / #2ee37a dark), 10.5px/0.22em uppercase accent meta,
 * hairline 1px rgba(15,22,20,0.06) dividers, 16px card radius, soft
 * shadows. Plain-English labels with help icons; status legend +
 * "Show technical details" toggle hide jargon by default.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  Paper,
  useMediaQuery,
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EventNote as CalendarIcon,
  HelpOutline as HelpIcon,
  WifiOff as WifiOffIcon,
  Email as EmailIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// ── Status helpers ────────────────────────────────────────────────────────────
// Plain-English bucket: each company collapses into one of four buckets
// so simple users see a single colored dot + label per row.
const HEALTH = {
  HEALTHY:     { color: "#25d366", label: "Healthy" },
  RECONNECTING:{ color: "#f59e0b", label: "Reconnecting" },
  OFFLINE:     { color: "#ef4444", label: "Offline" },
  NONE:        { color: "#a1a1aa", label: "Not set up" },
};

const bucketFor = (company) => {
  const wa = company.whatsapp;
  if (wa.total === 0) return "NONE";
  if (wa.connected === wa.total) return "HEALTHY";
  if (wa.connected > 0 || wa.pending > 0) return "RECONNECTING";
  return "OFFLINE";
};

// Connection-level bucket — used inside the expanded technical details
// to keep the same color language at the per-WhatsApp-number level.
const CONN_BUCKET = {
  CONNECTED:    "HEALTHY",
  DISCONNECTED: "OFFLINE",
  TIMEOUT:      "OFFLINE",
  OPENING:      "RECONNECTING",
  PAIRING:      "RECONNECTING",
  qrcode:       "RECONNECTING",
};
const connBucket = (status) => CONN_BUCKET[status] || "NONE";

const calProviderLabel = (p) =>
  p === "google" ? "Google Calendar" : p === "microsoft" ? "Microsoft Outlook" : p || "—";

// Time-since helpers — converts ISO timestamp to "2 hours ago" so
// users don't have to parse 2024-06-03T10:32:45Z.
const timeSince = (date) => {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "—";
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

// === Design tokens (CRM cardStackMode v3, mirrored from Tags/Contacts) ===
const SP = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  hero: 48,
};
const TT = {
  min: 44,
  primary: 48,
  hero: 56,
};
const RAD = {
  pill: 999,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
};
const COL = {
  brandGreen: '#25d366',
  brandGreenDeep: '#128c7e',
  brandGreenSoft: 'rgba(37,211,102,0.08)',
  textPrimary: '#0B0D0C',
  textSecondary: '#52525b',
  borderSoft: '#eaeaea',
  pageBg: '#f7faf8',
  cardBg: '#ffffff',
  destructive: '#ef4444',
  warning: '#f59e0b',
};

// ── Styles ────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surface    = isDark ? "#13181a" : "#ffffff";

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: SP.md,
      maxWidth: "100%",
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      [theme.breakpoints.down("xs")]: {
        overflowX: "hidden",
        gap: SP.sm,
      },
    },

    // ── Page header ────────────────────────────────────────────────────
    pageHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: SP.md,
      marginBottom: SP.xxs,
      minWidth: 0,
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: SP.sm,
      },
    },
    headerTextBlock: {
      minWidth: 0,
      flex: "1 1 auto",
    },
    headingDisplay: {
      fontWeight: 700,
      fontSize: 22,
      lineHeight: 1.25,
      letterSpacing: "-0.012em",
      color: fg,
      overflowWrap: "anywhere",
      wordBreak: "break-word",
      [theme.breakpoints.down("xs")]: {
        fontSize: 19,
      },
    },
    subheadingMuted: {
      fontSize: 13.5,
      lineHeight: 1.5,
      color: fgMuted,
      marginTop: 4,
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    },
    refreshGroup: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        justifyContent: "space-between",
        width: "100%",
      },
    },
    lastUpdated: {
      fontSize: 12,
      color: fgMuted,
      fontFeatureSettings: '"tnum" 1',
      whiteSpace: "nowrap",
    },
    refreshBtn: {
      color: fgMuted,
      border: `1px solid ${hair}`,
      borderRadius: RAD.md,
      padding: 8,
      width: TT.min,
      height: TT.min,
      transition: "color 0.14s ease, background 0.14s ease, border-color 0.14s ease",
      "&:hover": {
        color: accent,
        background: accentSoft,
        borderColor: accent,
      },
    },

    // ── Status legend ──────────────────────────────────────────────────
    legendBar: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: SP.md,
      padding: `${SP.sm}px ${SP.md}px`,
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.md,
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.03) inset"
        : "0 1px 0 rgba(15,22,20,0.02)",
      [theme.breakpoints.down("xs")]: {
        gap: SP.sm,
        padding: SP.sm,
      },
    },
    legendIntro: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      marginInlineEnd: SP.xs,
      whiteSpace: "nowrap",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        marginInlineEnd: 0,
        whiteSpace: "normal",
      },
    },
    legendItem: {
      display: "inline-flex",
      alignItems: "center",
      gap: SP.xs,
      minWidth: 0,
      whiteSpace: "nowrap",
    },
    legendDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
    },
    legendLabel: {
      fontSize: 12.5,
      fontWeight: 500,
      color: fg,
    },

    // ── Stats row ──────────────────────────────────────────────────────
    statsRow: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: SP.sm,
      maxWidth: "100%",
      width: "100%",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: SP.sm,
      },
    },
    statCard: {
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      padding: `${SP.md}px ${SP.md}px`,
      display: "flex",
      flexDirection: "column",
      gap: SP.xxs,
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.03) inset"
        : "0 1px 0 rgba(15,22,20,0.02)",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense card: 12px padding, 8px radius, hairline border, near-flat shadow
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.4)"
          : "0 1px 2px rgba(0,0,0,0.03)",
      },
    },
    statValue: {
      fontWeight: 700,
      fontSize: 28,
      color: fg,
      lineHeight: 1,
      letterSpacing: "-0.02em",
      fontFeatureSettings: '"tnum" 1',
      [theme.breakpoints.down("xs")]: {
        // Linear-dense hero token: capped at 16px (was 24, original 28)
        fontSize: 16,
        lineHeight: "20px",
        fontWeight: 600,
        color: fg,
        letterSpacing: "-0.01em",
      },
    },
    statLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      marginTop: 6,
      // Desktop: prevent per-character wrap from letter-spacing gaps inside
      // minmax(0,1fr) columns. Allow word-level break only if a token
      // literally cannot fit; never break inside a word.
      whiteSpace: "normal",
      overflowWrap: "break-word",
      wordBreak: "normal",
      hyphens: "none",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV label: 12/14 weight 500 muted, sentence-case
        fontSize: 12,
        lineHeight: "14px",
        fontWeight: 500,
        color: fgMuted,
        letterSpacing: "0.01em",
        textTransform: "none",
        marginTop: 4,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
      },
    },

    // ── Tech-detail toggle ─────────────────────────────────────────────
    techToggle: {
      display: "inline-flex",
      alignItems: "center",
      gap: SP.xs,
      alignSelf: "flex-start",
      padding: `${SP.xs}px ${SP.sm}px`,
      background: "transparent",
      border: `1px solid ${hair}`,
      borderRadius: RAD.pill,
      cursor: "pointer",
      fontSize: 12.5,
      fontWeight: 500,
      color: fgMuted,
      transition: "color 0.14s ease, background 0.14s ease, border-color 0.14s ease",
      "&:hover": {
        color: accent,
        background: accentSoft,
        borderColor: accent,
      },
      "& svg": {
        fontSize: 16,
      },
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
        flexDirection: "row",
        alignItems: "center",
        paddingInlineStart: SP.md,
        paddingInlineEnd: SP.md,
      },
    },

    // ── Main Paper wrapper (CRM cardStackMode v3) ──────────────────────
    mainPaper: {
      flex: 1,
      padding: 0,
      overflow: "hidden",
      borderRadius: 18,
      background: surface,
      border: `1px solid ${hair}`,
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
        : "0 14px 36px rgba(15,22,20,0.06), 0 2px 6px rgba(15,22,20,0.04)",
      maxWidth: "100%",
      width: "100%",
      boxSizing: "border-box",
      "& .MuiTable-root": {
        borderCollapse: "separate",
        borderSpacing: 0,
      },
      "& .MuiTableHead-root .MuiTableCell-head": {
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: accent,
        background: isDark ? "rgba(46,227,122,0.04)" : "rgba(18,140,126,0.04)",
        borderBottom: `1px solid ${hair}`,
        padding: "16px 18px",
        whiteSpace: "nowrap",
      },
      "& .MuiTableBody-root .MuiTableCell-body": {
        fontSize: 13.5,
        fontWeight: 500,
        color: fg,
        borderBottom: `1px solid ${hair}`,
        padding: "16px 18px",
      },
      "& .MuiTableBody-root .MuiTableRow-root": {
        transition: "background 0.14s ease",
        "&:hover": {
          background: accentSoft,
        },
        "&:last-child .MuiTableCell-body": { borderBottom: "none" },
      },
      [theme.breakpoints.down("sm")]: { borderRadius: 14 },
    },

    // ── cardStackMode v3 (table → card-stack on xs) ────────────────────
    cardStackMode: {
      [theme.breakpoints.down('xs')]: {
        '& thead': { display: 'none' },
        '& tbody': {
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: '100%',
          padding: SP.sm,
          boxSizing: 'border-box',
        },
        '& tr': {
          // Linear-dense card: 12px padding, hairline border, 8px radius, near-flat shadow
          display: 'block',
          width: '100%',
          background: surface,
          borderRadius: 8,
          padding: '12px',
          border: `1px solid ${hair}`,
          boxShadow: isDark
            ? '0 1px 2px rgba(0,0,0,0.4)'
            : '0 1px 2px rgba(0,0,0,0.03)',
          boxSizing: 'border-box',
          '&:hover': { background: surface },
        },
        '& td': {
          // KV-pair layout: stacked rows label/value
          display: 'block',
          width: '100%',
          padding: '4px 0',
          borderBottom: 'none',
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 400,
          textAlign: 'start',
          color: fg,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          '&:before': {
            // Linear-dense KV label: 12/14 weight 500 muted sentence-case
            content: 'attr(data-label)',
            display: 'block',
            fontSize: 12,
            lineHeight: '14px',
            fontWeight: 500,
            color: fgMuted,
            textTransform: 'none',
            letterSpacing: '0.01em',
            marginBottom: 3,
          },
          '&[data-label=""]:before, &:not([data-label]):before': {
            content: '""',
            display: 'none',
          },
          '&[data-label=""], &:not([data-label])': {
            display: 'block',
            paddingTop: 0,
          },
        },
      },
    },

    // ── Per-company desktop row decoration ─────────────────────────────
    companyHeader: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      minWidth: 0,
      flex: "1 1 auto",
    },
    companyIdentity: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      gap: 2,
    },
    companyName: {
      fontWeight: 700,
      fontSize: 15,
      lineHeight: 1.25,
      color: fg,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense title token: 14/18 weight 500, wrap not truncate
        fontSize: 14,
        lineHeight: "18px",
        fontWeight: 500,
        color: fg,
        letterSpacing: "-0.005em",
        whiteSpace: "normal",
        display: "block",
        WebkitLineClamp: "unset",
        WebkitBoxOrient: "unset",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        overflow: "visible",
      },
    },
    companyId: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      fontFeatureSettings: '"tnum" 1',
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta token: 11/14 weight 400 muted, no accent on chrome
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: fgMuted,
        letterSpacing: "0em",
        textTransform: "none",
      },
    },

    // ── Status pill (large dot + plain-English label) ──────────────────
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: SP.xs,
      paddingBlock: 6,
      paddingInlineStart: SP.xs,
      paddingInlineEnd: SP.sm,
      borderRadius: RAD.pill,
      border: `1px solid ${hair}`,
      background: surface,
      maxWidth: "100%",
      minWidth: 92,
      whiteSpace: "nowrap",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        // Linear-dense status pill: 2px 6px, radius 4, 10/13 weight 500
        padding: "2px 6px",
        paddingBlock: 2,
        paddingInlineStart: 6,
        paddingInlineEnd: 6,
        borderRadius: 4,
        gap: 4,
      },
    },
    statusDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35), 0 0 0 2px rgba(0,0,0,0)",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense at-a-glance: 7px circle, no inset highlight
        width: 7,
        height: 7,
        boxShadow: "none",
      },
    },
    statusLabel: {
      fontSize: 13,
      fontWeight: 600,
      color: fg,
      letterSpacing: "-0.005em",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense status pill label: 10/13 weight 500
        fontSize: 10,
        lineHeight: "13px",
        fontWeight: 500,
        letterSpacing: "0.04em",
        color: fg,
      },
    },
    statusCell: {
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("xs")]: {
        justifyContent: "flex-start",
      },
    },

    // ── 2x2 KPI grid inside each row/card ──────────────────────────────
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: SP.sm,
      width: "100%",
      [theme.breakpoints.down("xs")]: {
        gap: SP.xs,
      },
    },
    kpiTile: {
      display: "flex",
      flexDirection: "column",
      gap: 3,
      padding: `${SP.xs}px ${SP.sm}px`,
      background: accentSoft,
      borderRadius: RAD.md,
      border: `1px solid ${hair}`,
      minWidth: 0,
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV tile: transparent bg, no accent on chrome, tight stack
        padding: "6px 8px",
        background: "transparent",
        border: "none",
        borderRadius: 0,
        minHeight: 0,
        gap: 3,
      },
    },
    kpiTileLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      // Desktop: word-level break only; never break inside a token. This
      // prevents per-character wrap when 0.22em letter-spacing collides
      // with narrow kpiGrid columns at minmax(0,1fr).
      whiteSpace: "normal",
      overflowWrap: "break-word",
      wordBreak: "normal",
      hyphens: "none",
      minWidth: 0,
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV label: 12/14 weight 500 muted, sentence-case, no uppercase
        fontSize: 12,
        lineHeight: "14px",
        fontWeight: 500,
        color: fgMuted,
        letterSpacing: "0.01em",
        textTransform: "none",
        whiteSpace: "normal",
        "& .MuiSvgIcon-root": {
          display: "none",
        },
      },
    },
    kpiTileValue: {
      fontSize: 14,
      fontWeight: 600,
      color: fg,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontFeatureSettings: '"tnum" 1',
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV value: 12/16 weight 400, wrap, never truncate
        fontSize: 12,
        lineHeight: "16px",
        fontWeight: 400,
        color: fg,
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        display: "block",
        WebkitLineClamp: "unset",
        WebkitBoxOrient: "unset",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      },
    },
    helpIcon: {
      fontSize: 13,
      color: fgMuted,
      cursor: "help",
      verticalAlign: "middle",
    },

    // ── Calendar inline display ────────────────────────────────────────
    calLine: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 13.5,
      color: fg,
      "& svg": { fontSize: 16, color: fgMuted },
    },
    calOwner: {
      fontSize: 11.5,
      color: fgMuted,
      marginTop: 4,
      fontFeatureSettings: '"tnum" 1',
    },

    // ── Empty state (friendly) ─────────────────────────────────────────
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: SP.sm,
      padding: `${SP.hero}px ${SP.lg}px ${SP.xl}px`,
      textAlign: "center",
    },
    emptyStateIcon: {
      width: 72,
      height: 72,
      borderRadius: "50%",
      background: accentSoft,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": {
        fontSize: 36,
        color: accent,
      },
    },
    emptyStateTitle: {
      fontSize: 16,
      fontWeight: 700,
      color: fg,
      letterSpacing: "-0.005em",
    },
    emptyStateText: {
      fontSize: 13.5,
      lineHeight: 1.5,
      color: fgMuted,
      maxWidth: 320,
      margin: 0,
    },
    emptyStateMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      whiteSpace: "nowrap",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // ── Expanded technical detail ──────────────────────────────────────
    expandRow: {
      "& td": { padding: 0, borderBottom: "none" },
    },
    detailWrap: {
      background: accentSoft,
      borderTop: `1px solid ${hair}`,
      boxSizing: "border-box",
      [theme.breakpoints.down("sm")]: {
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        maxWidth: "100vw",
        "& table": { minWidth: "max-content" },
        "& th, & td": { whiteSpace: "nowrap" },
      },
      [theme.breakpoints.down("xs")]: {
        overflowX: "visible",
        maxWidth: "100%",
        "& table": { minWidth: "unset" },
        "& th, & td": { whiteSpace: "normal" },
      },
    },
    detailTable: {
      tableLayout: "fixed",
      width: "100%",
      "& th": {
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.22em",
        color: accent,
        padding: "10px 16px 10px 32px",
        background: "transparent",
        borderBottom: `1px solid ${hair}`,
        // Desktop: keep per-token integrity; allow only word-level breaks.
        whiteSpace: "normal",
        overflowWrap: "break-word",
        wordBreak: "normal",
        hyphens: "none",
      },
      "& td": {
        fontSize: 12.5,
        padding: "10px 16px 10px 32px",
        color: fg,
        borderBottom: `1px solid ${hair}`,
        overflowWrap: "break-word",
        wordBreak: "normal",
      },
      "& tr:last-child td": { borderBottom: "none" },
      [theme.breakpoints.down('xs')]: {
        '& thead': { display: 'none' },
        '& tbody': {
          display: 'flex',
          flexDirection: 'column',
          gap: SP.xs,
          padding: SP.sm,
          width: '100%',
        },
        '& tr': {
          display: 'block',
          background: surface,
          borderRadius: RAD.md,
          padding: SP.sm,
          border: `1px solid ${hair}`,
          boxSizing: 'border-box',
        },
        '& td': {
          display: 'block',
          width: '100%',
          padding: '4px 0',
          borderBottom: 'none',
          fontSize: 12.5,
          lineHeight: 1.4,
          textAlign: 'start',
          color: fg,
          '&:before': {
            content: 'attr(data-label)',
            display: 'block',
            fontSize: 10,
            fontWeight: 700,
            color: accent,
            textTransform: 'uppercase',
            letterSpacing: '0.22em',
            marginBottom: 2,
          },
          '&[data-label=""]:before, &:not([data-label]):before': {
            content: '""',
            display: 'none',
          },
        },
      },
    },
    monoNumber: {
      fontFamily: "Roboto, monospace",
      fontFeatureSettings: '"tnum" 1',
      fontSize: 13,
      color: fgMuted,
    },

    loadingWrap: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 48,
    },
    errorBanner: {
      padding: `${SP.sm}px ${SP.md}px`,
      borderRadius: RAD.md,
      background: isDark ? "rgba(239,68,68,0.10)" : "rgba(239,68,68,0.06)",
      border: `1px solid ${isDark ? "rgba(239,68,68,0.32)" : "rgba(239,68,68,0.24)"}`,
      color: isDark ? "#ff6b6b" : "#b91c1c",
      fontSize: 13,
    },
  };
});

// ── CompanyRow (expandable) ───────────────────────────────────────────────────
const CompanyRow = ({ company, showTechnical }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const hasConnections = company.whatsapp.total > 0;

  const bucket = bucketFor(company);
  const health = HEALTH[bucket];
  const calConnected = company.calendar.connected;

  // Derive plain-English "Last active" — pick the most recent connection
  // updatedAt across this tenant's WhatsApp numbers.
  const lastActiveISO = useMemo(() => {
    const dates = (company.whatsapp.connections || [])
      .map((c) => c.updatedAt)
      .filter(Boolean)
      .sort()
      .reverse();
    return dates[0] || null;
  }, [company.whatsapp.connections]);

  return (
    <>
      <TableRow>
        {/* Company name + ID */}
        <TableCell data-label={i18n.t("adminMonitor.col.company", { defaultValue: "Tenant" })}>
          <div className={classes.companyHeader}>
            <div className={classes.companyIdentity}>
              <span className={classes.companyName}>{company.name}</span>
              <span className={classes.companyId}>#{company.id}</span>
            </div>
          </div>
        </TableCell>

        {/* Status pill */}
        <TableCell data-label={i18n.t("adminMonitor.col.status", { defaultValue: "Status" })}>
          <div className={classes.statusCell}>
            <span className={classes.statusPill}>
              <span
                className={classes.statusDot}
                style={{ background: health.color }}
              />
              <span className={classes.statusLabel}>
                {i18n.t(`adminMonitor.health.${bucket.toLowerCase()}`, { defaultValue: health.label })}
              </span>
            </span>
          </div>
        </TableCell>

        {/* KPI grid (active sessions / messages today / last active / calendar) */}
        <TableCell data-label={i18n.t("adminMonitor.col.details", { defaultValue: "Connection details" })}>
          <div className={classes.kpiGrid}>
            <div className={classes.kpiTile}>
              <span className={classes.kpiTileLabel}>
                {i18n.t("adminMonitor.kpi.activeSessions", { defaultValue: "Active sessions" })}
                <Tooltip
                  title={i18n.t("adminMonitor.kpi.activeSessionsHelp", {
                    defaultValue: "How many WhatsApp numbers are currently online for this tenant.",
                  })}
                  arrow
                >
                  <HelpIcon className={classes.helpIcon} />
                </Tooltip>
              </span>
              <span className={classes.kpiTileValue}>
                {company.whatsapp.connected}/{company.whatsapp.total || 0}
              </span>
            </div>

            <div className={classes.kpiTile}>
              <span className={classes.kpiTileLabel}>
                {i18n.t("adminMonitor.kpi.lastActive", { defaultValue: "Last active" })}
                <Tooltip
                  title={i18n.t("adminMonitor.kpi.lastActiveHelp", {
                    defaultValue: "Most recent time a WhatsApp number reported its status.",
                  })}
                  arrow
                >
                  <HelpIcon className={classes.helpIcon} />
                </Tooltip>
              </span>
              <span className={classes.kpiTileValue}>{timeSince(lastActiveISO)}</span>
            </div>

            <div className={classes.kpiTile}>
              <span className={classes.kpiTileLabel}>
                {i18n.t("adminMonitor.kpi.calendar", { defaultValue: "Calendar" })}
              </span>
              <span className={classes.kpiTileValue}>
                {calConnected ? (
                  <span className={classes.calLine}>
                    {company.calendar.provider === "microsoft" ? <EmailIcon /> : <CalendarIcon />}
                    {calProviderLabel(company.calendar.provider)}
                  </span>
                ) : (
                  i18n.t("adminMonitor.noCalendar", { defaultValue: "Not connected" })
                )}
              </span>
              {calConnected && company.calendar.ownerEmail && (
                <span className={classes.calOwner}>{company.calendar.ownerEmail}</span>
              )}
            </div>

            <div className={classes.kpiTile}>
              <span className={classes.kpiTileLabel}>
                {i18n.t("adminMonitor.kpi.channel", { defaultValue: "Channel" })}
                <Tooltip
                  title={i18n.t("adminMonitor.kpi.channelHelp", {
                    defaultValue: "Which WhatsApp channel this tenant uses (Cloud API or WhatsApp Web).",
                  })}
                  arrow
                >
                  <HelpIcon className={classes.helpIcon} />
                </Tooltip>
              </span>
              <span className={classes.kpiTileValue}>
                {company.whatsapp.hasMetaApi
                  ? i18n.t("adminMonitor.kpi.metaApi", { defaultValue: "Meta Cloud API" })
                  : company.whatsapp.total > 0
                  ? i18n.t("adminMonitor.kpi.whatsappWeb", { defaultValue: "WhatsApp Web" })
                  : "—"}
              </span>
            </div>
          </div>
        </TableCell>

        {/* Expand toggle — only when technical details mode is on */}
        <TableCell data-label="">
          {hasConnections && showTechnical && (
            <Tooltip
              title={
                expanded
                  ? i18n.t("adminMonitor.hideDetails", { defaultValue: "Hide technical details" })
                  : i18n.t("adminMonitor.showDetails", { defaultValue: "Show technical details" })
              }
            >
              <IconButton
                size="small"
                onClick={() => setExpanded((v) => !v)}
                className={classes.refreshBtn}
                style={{ width: 36, height: 36 }}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded detail rows */}
      {hasConnections && showTechnical && (
        <TableRow className={classes.expandRow}>
          <TableCell colSpan={4} style={{ padding: 0 }} data-label="">
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <div className={classes.detailWrap}>
                <Table size="small" className={classes.detailTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("adminMonitor.detail.name",   { defaultValue: "Connection name" })}</TableCell>
                      <TableCell>{i18n.t("adminMonitor.detail.status", { defaultValue: "Status" })}</TableCell>
                      <TableCell>{i18n.t("adminMonitor.detail.channel",{ defaultValue: "Channel" })}</TableCell>
                      <TableCell>{i18n.t("adminMonitor.detail.updated",{ defaultValue: "Last update" })}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {company.whatsapp.connections.map((conn) => {
                      const cBucket = connBucket(conn.status);
                      const cHealth = HEALTH[cBucket];
                      return (
                        <TableRow key={conn.id}>
                          <TableCell
                            data-label={i18n.t("adminMonitor.detail.name", { defaultValue: "Connection name" })}
                          >
                            {conn.name || (
                              <span className={classes.monoNumber}>#{conn.id}</span>
                            )}
                          </TableCell>
                          <TableCell
                            data-label={i18n.t("adminMonitor.detail.status", { defaultValue: "Status" })}
                          >
                            <span className={classes.statusPill}>
                              <span
                                className={classes.statusDot}
                                style={{ background: cHealth.color }}
                              />
                              <span className={classes.statusLabel}>{cHealth.label}</span>
                            </span>
                          </TableCell>
                          <TableCell
                            data-label={i18n.t("adminMonitor.detail.channel", { defaultValue: "Channel" })}
                          >
                            {conn.channel || "—"}
                          </TableCell>
                          <TableCell
                            data-label={i18n.t("adminMonitor.detail.updated", { defaultValue: "Last update" })}
                          >
                            <span className={classes.monoNumber}>
                              {timeSince(conn.updatedAt)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
const AdminConnectionsMonitor = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [data,    setData]    = useState(null);   // { companies: [...] }
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [tickNow, setTickNow] = useState(Date.now());
  const [showTechnical, setShowTechnical] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await api.get("/api/admin/connections-overview");
      setData(resp);
      setLastLoadedAt(new Date());
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        i18n.t("adminMonitor.loadError", { defaultValue: "Could not load connections overview." })
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tick every 30s so "Updated 30 seconds ago" stays live without
  // re-fetching the API.
  useEffect(() => {
    const t = setInterval(() => setTickNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const updatedAgo = useMemo(() => {
    if (!lastLoadedAt) return null;
    return timeSince(lastLoadedAt);
    // tickNow forces recompute every 30s
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastLoadedAt, tickNow]);

  // ── Derived stats ──────────────────────────────────────────────────────
  const stats = (() => {
    if (!data?.companies) return null;
    const cs = data.companies;
    return {
      total:          cs.length,
      withWhatsApp:   cs.filter((c) => c.whatsapp.connected > 0).length,
      withCalendar:   cs.filter((c) => c.calendar.connected).length,
      disconnected:   cs.filter((c) => c.whatsapp.total > 0 && c.whatsapp.connected === 0).length,
    };
  })();

  // Legend entries — colored dot + plain label, repeated up top so users
  // can decode the per-row pills.
  const legendEntries = [
    { key: "HEALTHY",      ...HEALTH.HEALTHY },
    { key: "RECONNECTING", ...HEALTH.RECONNECTING },
    { key: "OFFLINE",      ...HEALTH.OFFLINE },
    { key: "NONE",         ...HEALTH.NONE },
  ];

  return (
    <div className={classes.root}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className={classes.pageHeader}>
        <div className={classes.headerTextBlock}>
          <div className={classes.headingDisplay}>
            {i18n.t("adminMonitor.title", { defaultValue: "Connections Overview" })}
          </div>
          <div className={classes.subheadingMuted}>
            {i18n.t("adminMonitor.subtitle", {
              defaultValue:
                "See at a glance which tenants are online, reconnecting, or offline.",
            })}
          </div>
        </div>
        <div className={classes.refreshGroup}>
          {updatedAgo && (
            <span className={classes.lastUpdated}>
              {i18n.t("adminMonitor.updatedAgo", {
                defaultValue: "Updated {{ago}}",
              }).replace("{{ago}}", updatedAgo)}
            </span>
          )}
          <Tooltip title={i18n.t("adminMonitor.refresh", { defaultValue: "Refresh" })}>
            <span>
              <IconButton
                className={classes.refreshBtn}
                onClick={load}
                disabled={loading}
                size="small"
              >
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── Status legend ─────────────────────────────────────────────── */}
      <div className={classes.legendBar}>
        <span className={classes.legendIntro}>
          {i18n.t("adminMonitor.legend.title", { defaultValue: "What each color means" })}
        </span>
        {legendEntries.map((e) => (
          <span key={e.key} className={classes.legendItem}>
            <span className={classes.legendDot} style={{ background: e.color }} />
            <span className={classes.legendLabel}>
              {i18n.t(`adminMonitor.health.${e.key.toLowerCase()}`, { defaultValue: e.label })}
            </span>
          </span>
        ))}
      </div>

      {/* ── Stats row ─────────────────────────────────────────────────── */}
      {stats && (
        <div className={classes.statsRow}>
          {[
            { value: stats.total,        label: i18n.t("adminMonitor.stat.total",       { defaultValue: "Total tenants" }) },
            { value: stats.withWhatsApp, label: i18n.t("adminMonitor.stat.whatsapp",    { defaultValue: "WhatsApp online" }) },
            { value: stats.withCalendar, label: i18n.t("adminMonitor.stat.calendar",    { defaultValue: "Calendar connected" }) },
            { value: stats.disconnected, label: i18n.t("adminMonitor.stat.disconnected",{ defaultValue: "Tenants offline" }) },
          ].map(({ value, label }) => (
            <div key={label} className={classes.statCard}>
              <div className={classes.statValue}>
                {value}
              </div>
              <div className={classes.statLabel}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tech-details toggle ──────────────────────────────────────── */}
      <button
        type="button"
        className={classes.techToggle}
        onClick={() => setShowTechnical((v) => !v)}
      >
        {showTechnical ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        {showTechnical
          ? i18n.t("adminMonitor.hideTechnical", { defaultValue: "Hide technical details" })
          : i18n.t("adminMonitor.showTechnical", { defaultValue: "Show technical details" })}
      </button>

      {/* ── Error state ───────────────────────────────────────────────── */}
      {error && (
        <Typography className={classes.errorBanner}>
          {error}
        </Typography>
      )}

      {/* ── Main table ────────────────────────────────────────────────── */}
      {loading && !data ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={32} />
        </div>
      ) : (
        <Paper className={classes.mainPaper} elevation={0}>
          {data?.companies?.length === 0 ? (
            <div className={classes.emptyState}>
              <span className={classes.emptyStateIcon}>
                <WifiOffIcon />
              </span>
              <div className={classes.emptyStateMeta}>
                {i18n.t("adminMonitor.emptyMeta", { defaultValue: "Nothing connected yet" })}
              </div>
              <div className={classes.emptyStateTitle}>
                {i18n.t("adminMonitor.emptyTitle", {
                  defaultValue: "No WhatsApp connections yet.",
                })}
              </div>
              <p className={classes.emptyStateText}>
                {i18n.t("adminMonitor.emptyText", {
                  defaultValue:
                    "Add a WhatsApp number from Settings → Connections to start monitoring tenant health here.",
                })}
              </p>
            </div>
          ) : (
            <Table className={classes.cardStackMode}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {i18n.t("adminMonitor.col.company", { defaultValue: "Tenant" })}
                  </TableCell>
                  <TableCell>
                    {i18n.t("adminMonitor.col.status", { defaultValue: "Status" })}
                  </TableCell>
                  <TableCell>
                    {i18n.t("adminMonitor.col.details", { defaultValue: "Connection details" })}
                  </TableCell>
                  <TableCell aria-label="actions" />
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.companies?.map((company) => (
                  <CompanyRow
                    key={company.id}
                    company={company}
                    showTechnical={showTechnical}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
      {/* isMobile preserved for future mobile-only branches */}
      {false && isMobile && null}
    </div>
  );
};

export default AdminConnectionsMonitor;
